import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';

import { API_ENDPOINT, SupervisionNoteStatus } from '../../constants';
import { fetchWrapper, history, toUtcOffsetDisplay } from '../../helpers';
import type {
  DocumentAggregation,
  IClientSupervision,
  ISessionWithClaimStatus,
  ISupervisionWithClaimStatus,
  ITargetPhase,
} from '../../utils/types';
import { getBayValueWithoutPrefix, getSupervisionSearchStr } from '../../utils/common';
import {
  ServiceCodes,
  SessionClaimStatus,
} from '../../components/layout/billings/claims/constants';
import { asDefinedOr } from '../../components/layout/billings/claims/utils';
import {
  getClientSupervisionNarrativeSourceKey,
  getNarrativeSuggestionContextKey,
} from '../../utils/narrativeSuggestion';
import type {
  NarrativeSuggestionContext,
} from '../../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'client_supervisions';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract until typed.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ClientSupervisionState>) =>
    createExtraReducers(builder),
});

export interface ClientSupervisionState {
  list: Array<IClientSupervision>;
  error: any;
  loading: boolean;
  narrativeSuggestionLoading: boolean;
  lastAiGeneratedSessionNarrative: string | null;
  narrativeSuggestions: Record<string, string>;
  narrativeSuggestionRequest: (NarrativeSuggestionContext & { requestId: string }) | null;
  narrativeContextToken: string | null;
  narrativeUpdateRequest: {
    supervisionId?: number;
    narrativeContext?: NarrativeSuggestionContext;
    requestId: string;
  } | null;
  clientAggregations: DocumentAggregation | null;
  current: IClientSupervision | null;
  // Latest dispatched getSupervision fetch. Guards fulfilled/rejected so a stale/out-of-order
  // detail response (e.g. note A's response arriving after note B is already active) never
  // overwrites the currently active note.
  detailRequestId: string | null;
  supervisionsWithClaimStatus: ISessionWithClaimStatus[];
}

function createInitialState(): ClientSupervisionState {
  return {
    list: [],
    loading: false,
    narrativeSuggestionLoading: false,
    lastAiGeneratedSessionNarrative: null,
    narrativeSuggestions: {},
    narrativeSuggestionRequest: null,
    narrativeContextToken: null,
    narrativeUpdateRequest: null,
    clientAggregations: null,
    error: null,
    current: null,
    detailRequestId: null,
    supervisionsWithClaimStatus: [],
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
    setNarrativeSuggestionContext,
  };

  function clearState(state: ClientSupervisionState) {
    state.list = [];
    state.current = null;
    state.detailRequestId = null;
    state.clientAggregations = null;
    state.error = null;
    state.loading = false;
    state.narrativeSuggestionLoading = false;
    state.lastAiGeneratedSessionNarrative = null;
    state.narrativeSuggestions = {};
    state.narrativeSuggestionRequest = null;
    state.narrativeContextToken = null;
    state.narrativeUpdateRequest = null;
  }

  type ClientSupervisionStateUpdate = {
    [Key in keyof ClientSupervisionState]: { key: Key; data: ClientSupervisionState[Key] };
  }[keyof ClientSupervisionState];

  function updateState(
    state: ClientSupervisionState,
    action: PayloadAction<ClientSupervisionStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function setNarrativeSuggestionContext(
    state: ClientSupervisionState,
    action: PayloadAction<NarrativeSuggestionContext>,
  ) {
    const context = action.payload;
    if (state.current?.id !== context.noteId) return;

    const key = getNarrativeSuggestionContextKey(context);
    state.narrativeContextToken = key;
    state.lastAiGeneratedSessionNarrative = state.narrativeSuggestions[key] ?? null;
    if (
      state.narrativeSuggestionRequest &&
      (state.narrativeSuggestionRequest.noteId !== context.noteId ||
        state.narrativeSuggestionRequest.noteType !== context.noteType ||
        state.narrativeSuggestionRequest.sourceKey !== context.sourceKey)
    ) {
      state.narrativeSuggestionLoading = false;
      state.narrativeSuggestionRequest = null;
    }
  }
}

function normalizeNarrativeSuggestionContext(
  context?: NarrativeSuggestionContext,
): NarrativeSuggestionContext | undefined {
  if (!context) return undefined;
  return {
    ...context,
    contextToken: getNarrativeSuggestionContextKey(context),
  };
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/client_supervisions`;

  return {
    getList: getList(),
    createSupervision: createSupervision(),
    getSupervision: getSupervision(),
    getNarrativeSuggestion: getNarrativeSuggestion(),
    updateSupervision: updateSupervision(),
    stopSupervision: stopSupervision(),
    createTargetPhase: createTargetPhase(),
    deleteTargetPhase: deleteTargetPhase(),
    getSupervisionsWithClaimStatus: getSupervisionsWithClaimStatus(),
    makeSupervisionEditable: makeSupervisionEditable(),
    backdateSupervision: backdateSupervision(),
    deleteSupervision: deleteSupervision(),
    getAggregations: getAggregations(),
    downloadPDF: downloadPDF(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  function createSupervision() {
    return createAsyncThunk(
      `${name}/createSupervision`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function getSupervision() {
    return createAsyncThunk(
      `${name}/getSupervision`,
      async ({ supervisionId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${supervisionId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function getNarrativeSuggestion() {
    return createAsyncThunk(
      `${name}/getNarrativeSuggestion`,
      async ({ supervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.post({
          url: `${API_ENDPOINT}/narrative_suggestion/97155/${supervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function getAggregations() {
    return createAsyncThunk(
      `${name}/getAggregations`,
      async ({ getAccessTokenSilently, clientId, start, end }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/data/utilization/?client_id=${clientId}&start=${start}&end=${end}`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function updateSupervision() {
    return createAsyncThunk(
      `${name}/updateSupervision`,
      async ({ supervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${supervisionId}/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function stopSupervision() {
    return createAsyncThunk(
      `${name}/stopSupervision`,
      async ({ supervisionId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${supervisionId}/stop/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function createTargetPhase() {
    return createAsyncThunk(
      `${name}/createTargetPhase`,
      async ({ supervisionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${supervisionId}/target_phase/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function deleteTargetPhase() {
    return createAsyncThunk(
      `${name}/deleteTargetPhase`,
      async ({ supervisionId, targetPhaseId, getAccessTokenSilently }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${supervisionId}/target_phase/${targetPhaseId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function getSupervisionsWithClaimStatus() {
    return createAsyncThunk(
      `${name}/getSupervisionsWithClaimStatus`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${baseUrl}/claim_history/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function deleteSupervision() {
    return createAsyncThunk(
      `${name}/deleteSupervision`,
      async ({ supervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${supervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function makeSupervisionEditable() {
    return createAsyncThunk(
      `${name}/makeSupervisionEditable`,
      async ({ supervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.put({
          url: `${baseUrl}/${supervisionId}/edit/`,
          getAccessTokenSilently,
        }),
    );
  }

  function backdateSupervision() {
    return createAsyncThunk(
      `${name}/backdateSupervision`,
      async ({ supervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          {
            url: `${baseUrl}/${supervisionId}/backdate/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function downloadPDF() {
    return createAsyncThunk(
      `${name}/downloadPDF`,
      async ({ supervisionId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${supervisionId}/download/`,
          getAccessTokenSilently,
        });
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ClientSupervisionState>) {
  const {
    getList,
    createSupervision,
    getSupervision,
    getNarrativeSuggestion,
    updateSupervision,
    stopSupervision,
    createTargetPhase,
    deleteTargetPhase,
    getSupervisionsWithClaimStatus,
    deleteSupervision,
    makeSupervisionEditable,
    backdateSupervision,
    getAggregations,
    downloadPDF,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getList.fulfilled,
      (state: ClientSupervisionState, action: PayloadAction<IClientSupervision[]>) => {
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: ClientSupervisionState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getAggregations()
    .addCase(getAggregations.pending, (state: ClientSupervisionState) => {
      state.error = null;
    })
    .addCase(getAggregations.rejected, (state: ClientSupervisionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    })

    // createSupervision()
    .addCase(createSupervision.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createSupervision.fulfilled,
      (state: ClientSupervisionState, action: PayloadAction<IClientSupervision>) => {
        state.loading = false;
        state.current = action.payload;
        state.list = [...state.list, action.payload];
      },
    )
    .addCase(createSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getSupervision()
    // Guard by requestId (mirrors the client.slice.ts modifier-matrix pattern) so a stale
    // detail response can never overwrite a newer note that is already active.
    .addCase(getSupervision.pending, (state: ClientSupervisionState, action: any) => {
      state.loading = true;
      state.error = null;
      state.current = null;
      state.detailRequestId = action.meta.requestId;
      state.narrativeSuggestionLoading = false;
      state.narrativeSuggestionRequest = null;
      state.narrativeContextToken = null;
      state.narrativeUpdateRequest = null;
    })
    .addCase(
      getSupervision.fulfilled,
      (
        state: ClientSupervisionState,
        action: PayloadAction<IClientSupervision> & { meta: { requestId: string } },
      ) => {
        if (action.meta.requestId !== state.detailRequestId) return;
        state.current = action.payload;
        state.narrativeContextToken = null;
        if (action.payload.id !== undefined) {
          const sourceKey = getClientSupervisionNarrativeSourceKey(action.payload);
          const contextKey = getNarrativeSuggestionContextKey({
            noteId: action.payload.id,
            noteType: '97155',
            sourceKey,
          });
          if (action.payload.last_ai_generated_session_narrative !== undefined) {
            if (action.payload.last_ai_generated_session_narrative === null) {
              delete state.narrativeSuggestions[contextKey];
            } else {
              state.narrativeSuggestions[contextKey] =
                action.payload.last_ai_generated_session_narrative;
            }
          }
          state.lastAiGeneratedSessionNarrative = state.narrativeSuggestions[contextKey] ?? null;
        } else {
          state.lastAiGeneratedSessionNarrative = null;
        }
        state.loading = false;
        state.error = null;
      },
    )
    .addCase(getSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      if (action.meta.requestId !== state.detailRequestId) return;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getNarrativeSuggestion()
    .addCase(getNarrativeSuggestion.pending, (state: ClientSupervisionState, action: any) => {
      const { noteId, noteType, sourceKey } = action.meta.arg;
      const context = normalizeNarrativeSuggestionContext(action.meta.arg);
      state.narrativeSuggestionRequest = {
        noteId,
        noteType,
        sourceKey,
        contextToken: context?.contextToken,
        requestId: action.meta.requestId,
      };
      state.narrativeSuggestionLoading = state.current?.id === noteId;
    })
    .addCase(
      getNarrativeSuggestion.fulfilled,
      (state: ClientSupervisionState, action: any) => {
        const { noteId, noteType, sourceKey } = action.meta.arg;
        const context = normalizeNarrativeSuggestionContext(action.meta.arg);
        const request = state.narrativeSuggestionRequest;
        if (
          state.current?.id === noteId &&
          request !== null &&
          request.requestId === action.meta.requestId &&
          request.noteType === noteType &&
          request.sourceKey === sourceKey &&
          request.contextToken === context?.contextToken
        ) {
          const contextKey = getNarrativeSuggestionContextKey({ noteId, noteType, sourceKey });
          state.narrativeSuggestions[contextKey] = action.payload.narrative;
          state.narrativeSuggestionLoading = false;
          state.lastAiGeneratedSessionNarrative = action.payload.narrative;
        }
      },
    )
    .addCase(getNarrativeSuggestion.rejected, (state: ClientSupervisionState, action: any) => {
      if (state.narrativeSuggestionRequest?.requestId === action.meta.requestId) {
        state.narrativeSuggestionLoading = false;
        state.narrativeSuggestionRequest = null;
      }
    })

    // updateSupervision()
    .addCase(updateSupervision.pending, (state: ClientSupervisionState, action: any) => {
      state.loading = true;
      state.error = null;
      const narrativeContext = normalizeNarrativeSuggestionContext(
        action.meta.arg.narrativeContext,
      );
      state.narrativeUpdateRequest = {
        supervisionId: action.meta.arg.supervisionId,
        narrativeContext,
        requestId: action.meta.requestId,
      };
    })
    .addCase(
      updateSupervision.fulfilled,
      (state: ClientSupervisionState, action: PayloadAction<IClientSupervision> & { meta: any }) => {
        const { supervisionId } = action.meta.arg;
        const narrativeContext = normalizeNarrativeSuggestionContext(
          action.meta.arg.narrativeContext,
        );
        const request = state.narrativeUpdateRequest;
        const isCurrentRequest =
          request?.requestId === action.meta.requestId &&
          request?.supervisionId === supervisionId &&
          state.current?.id === supervisionId;
        const isCurrentNarrativeContext =
          !narrativeContext ||
          (request?.narrativeContext?.noteId === narrativeContext.noteId &&
            request?.narrativeContext?.noteType === narrativeContext.noteType &&
            request?.narrativeContext?.sourceKey === narrativeContext.sourceKey &&
            request?.narrativeContext?.contextToken === narrativeContext.contextToken &&
            state.current?.id === narrativeContext.noteId &&
            state.narrativeContextToken === narrativeContext.contextToken);
        if (!isCurrentRequest || !isCurrentNarrativeContext) {
          if (request?.requestId === action.meta.requestId) {
            state.narrativeUpdateRequest = null;
            state.loading = false;
          }
          return;
        }
        state.current = action.payload;
        if (action.payload.id !== undefined && action.payload.last_ai_generated_session_narrative !== undefined) {
          const sourceKey = getClientSupervisionNarrativeSourceKey(action.payload);
          const contextKey = getNarrativeSuggestionContextKey({
            noteId: action.payload.id,
            noteType: '97155',
            sourceKey,
          });
          if (action.payload.last_ai_generated_session_narrative === null) {
            delete state.narrativeSuggestions[contextKey];
          } else {
            state.narrativeSuggestions[contextKey] =
              action.payload.last_ai_generated_session_narrative;
          }
          state.lastAiGeneratedSessionNarrative = state.narrativeSuggestions[contextKey] ?? null;
        }
        state.loading = false;
        state.error = null;
        state.narrativeUpdateRequest = null;
      },
    )
    .addCase(updateSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      if (state.narrativeUpdateRequest?.requestId !== action.meta.requestId) return;
      state.loading = false;
      state.error = action.error;
      state.narrativeUpdateRequest = null;
      toast.error(action.error.message);
    })

    // stopSupervision()
    .addCase(stopSupervision.pending, (state: ClientSupervisionState) => {
      state.error = null;
    })
    .addCase(
      stopSupervision.fulfilled,
      () => {
        toast.success('Client Supervision has been submitted');
        history.navigate('/dashboard');
      },
    )
    .addCase(stopSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    })

    // createTargetPhase()
    .addCase(createTargetPhase.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createTargetPhase.fulfilled,
      (state: ClientSupervisionState, action: PayloadAction<ITargetPhase>) => {
        if (state.current) {
          state.current.target_phase = [...(state.current.target_phase ?? []), action.payload];
        }
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(createTargetPhase.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // deleteTargetPhase()
    .addCase(deleteTargetPhase.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteTargetPhase.fulfilled, (state: ClientSupervisionState, action: any) => {
      if (state.current) {
        state.current.target_phase = state.current.target_phase?.filter(
          (targetPhase) => targetPhase.id !== action.meta.arg.targetPhaseId,
        );
      }
      state.loading = false;
      state.error = null;
    })
    .addCase(deleteTargetPhase.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // getSupervisionsWithClaimStatus()
    .addCase(getSupervisionsWithClaimStatus.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getSupervisionsWithClaimStatus.fulfilled,
      (state: ClientSupervisionState, action: PayloadAction<ISupervisionWithClaimStatus[]>) => {
        state.loading = false;
        state.supervisionsWithClaimStatus = action.payload.map(
          (supervision: ISupervisionWithClaimStatus) => {
            return {
              id: supervision.id,
              date: moment(supervision.date_start).format(),
              status:
                supervision.claim_history && supervision.claim_history?.length > 0
                  ? supervision.claim_history[0].status
                  : SessionClaimStatus.Unclaimed,
              selected: false,
              readonly: Boolean(supervision.claim_id),
              location: supervision.location,
              duration: supervision.expected_duration ?? 0,
              provider_name: `${supervision.bcba.first_name} ${supervision.bcba.last_name} `,
              provider_id: supervision.bcba.id,
              // SAFETY: BCBA rows always carry a numeric id in this claim-history response.
              author_id: supervision.bcba.id as number, // bcba is the author
              bay: getBayValueWithoutPrefix(supervision.bay),
              timeZone: supervision.utc_offset_date_start
                ? toUtcOffsetDisplay(asDefinedOr(supervision.utc_offset_date_start, 0))
                : '0:0',
              // SAFETY: API status values are restricted to the supervision-note status enum.
              sessionStatus: supervision.status as SupervisionNoteStatus,
              procedureCode: ServiceCodes.ClientSNProcedureCode,
            };
          },
        );
      },
    )
    .addCase(
      getSupervisionsWithClaimStatus.rejected,
      (state: ClientSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message, {
          toastId: 'client-supervisions-sessions-with-claim-status-error',
        });
      },
    )
    // deleteSupervision()
    .addCase(deleteSupervision.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteSupervision.fulfilled, (state: ClientSupervisionState) => {
      state.loading = false;
      state.current = null;
      toast.success('Client supervision deleted successfully');
    })
    .addCase(deleteSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // makeSupervisionEditable()
    .addCase(makeSupervisionEditable.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(makeSupervisionEditable.fulfilled, (state: ClientSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Client supervision note is now editable');
    })
    .addCase(makeSupervisionEditable.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    //backdateSupervision()

    .addCase(backdateSupervision.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(backdateSupervision.fulfilled, (state: ClientSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('The start date of Client supervision note has been updated');
    })
    .addCase(backdateSupervision.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // downloadPDF()
    .addCase(downloadPDF.pending, (state: ClientSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(downloadPDF.fulfilled, (state: ClientSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Client supervision downloaded successfully');
    })
    .addCase(downloadPDF.rejected, (state: ClientSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    });
}

export const clientSupervisionActions = { ...slice.actions, ...extraActions };
export const clientSupervisionReducer = slice.reducer;
